import { useMemo, useState } from 'react';

import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { ICartPrice, EOrderAsLastTimeState } from '@model';
import { isAdyenWebComponent, isPaypal, isPrzelewy24OrPaypo } from '@utils/payment';

import { ILastOrder } from '../../../model';
import { IOrderAsLastTimeProcess } from '../model';

import { useAdyenProcessForOaLT } from './adyenOrderProcess/useAdyenProcessForOaLT';
import { useParfumsProcessForOaLT } from './parfumsOrderProcess/useParfumsProcessForOaLT';
import { usePaypalProcessForOalt } from './paypalOrderProcess/usePaypalProcessForOalt';
import { usePrzelewyPaypoProcessForOalt } from './przelewyPaypoOrderProcess';

export const useOrderAsLastTimeProcess = (
  lastOrder: ILastOrder | null,
  cartPrice: ICartPrice,
  onClose: () => void,
  setLoading: (isLoading: boolean) => void,
  adyenClientKey?: string,
): IOrderAsLastTimeProcess => {
  const {
    applicationSettings: {
      features: { enablePrzelewyPaypoNewFlowOalt },
    },
  } = useApplicationRuntimeContext();

  const [pageState, setPageState] = useState<EOrderAsLastTimeState>(EOrderAsLastTimeState.INFO_PAGE);

  const adyen = useAdyenProcessForOaLT({
    setState: setPageState,
    lastOrder: lastOrder,
    cartPrice: cartPrice,
    clientKey: adyenClientKey,
    onClose: onClose,
  });

  const paypal = usePaypalProcessForOalt({
    setState: setPageState,
    lastOrder: lastOrder,
    cartPrice: cartPrice,
    onClose: onClose,
    setLoading,
  });

  const przelewyPaypo = usePrzelewyPaypoProcessForOalt({
    setState: setPageState,
    lastOrder: lastOrder,
    cartPrice: cartPrice,
    onClose: onClose,
    setLoading,
    pageState,
  });

  const parfums = useParfumsProcessForOaLT({
    setState: setPageState,
    lastOrder: lastOrder,
    cartPrice: cartPrice,
  });

  return useMemo(() => {
    const paymentGroups = lastOrder?.paymentInfo.paymentGroups ?? [];
    const paymentMethod =
      (isPaypal(paymentGroups) && paypal) ||
      (isAdyenWebComponent(paymentGroups) && adyen) ||
      (isPrzelewy24OrPaypo(paymentGroups) && enablePrzelewyPaypoNewFlowOalt && przelewyPaypo) ||
      parfums;

    return {
      onContinueToCheckout: paymentMethod.onContinueToCheckout,
      actionComponent: paymentMethod.actionComponent,
      pageState,
      setState: setPageState,
      setLoading,
    };
  }, [
    lastOrder?.paymentInfo.paymentGroups,
    paypal,
    adyen,
    przelewyPaypo,
    enablePrzelewyPaypoNewFlowOalt,
    parfums,
    pageState,
    setLoading,
  ]);
};
